import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Plus, Loader2, Trash2, TrendingUp } from "lucide-react";

export default function Performance() {
  const [channels, setChannels] = useState([]);
  const [selected, setSelected] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState("views");
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), views: "", subscribers: "", videos_published: "" });

  useEffect(() => {
    api.get("/channels").then(({ data }) => {
      setChannels(data);
      if (data.length) setSelected(data[0].id);
      setLoading(false);
    });
  }, []);

  const loadRows = async (cid) => {
    if (!cid) return;
    const { data } = await api.get("/performance", { params: { channel_id: cid } });
    setRows(data);
  };
  useEffect(() => { if (selected) loadRows(selected); }, [selected]);

  const add = async () => {
    if (!selected) { toast.error("Create a channel first"); return; }
    try {
      await api.post("/performance", {
        channel_id: selected,
        date: new Date(form.date).toISOString(),
        views: parseInt(form.views || 0, 10),
        subscribers: parseInt(form.subscribers || 0, 10),
        videos_published: parseInt(form.videos_published || 0, 10),
      });
      toast.success("Entry logged");
      setForm({ date: new Date().toISOString().slice(0, 10), views: "", subscribers: "", videos_published: "" });
      loadRows(selected);
    } catch { toast.error("Failed to log entry"); }
  };

  const del = async (id) => { await api.delete(`/performance/${id}`); loadRows(selected); };

  const chartData = rows.map((r) => ({
    date: new Date(r.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    views: r.views, subscribers: r.subscribers, videos_published: r.videos_published,
  }));

  const METRICS = [
    { key: "views", label: "Views", color: "#D9A441" },
    { key: "subscribers", label: "Subscribers", color: "#4182D9" },
    { key: "videos_published", label: "Videos", color: "#2E8B57" },
  ];
  const activeMetric = METRICS.find((m) => m.key === metric);

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-primary" size={28} /></div>;

  return (
    <div className="fade-up">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-mono text-xs text-primary tracking-[0.25em] uppercase mb-2">Growth tracking</p>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight">Performance</h1>
        </div>
        <select value={selected} onChange={(e) => setSelected(e.target.value)} data-testid="perf-channel-select"
          className="bg-[#1A1A1C] border border-border rounded-md px-3 py-2.5 text-sm outline-none focus:border-primary transition-colors duration-200">
          {channels.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </header>

      {/* Chart */}
      <section className="bg-[#121214] border border-border rounded-lg p-6 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-2">
            <TrendingUp size={18} className="text-primary" />
            <h2 className="font-display font-bold text-lg tracking-tight">Trend</h2>
          </div>
          <div className="flex gap-2">
            {METRICS.map((m) => (
              <button key={m.key} onClick={() => setMetric(m.key)} data-testid={`metric-${m.key}`}
                className={`text-xs font-medium rounded-md px-3 py-1.5 border transition-colors duration-200 ${metric === m.key ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                {m.label}
              </button>
            ))}
          </div>
        </div>
        {chartData.length === 0 ? (
          <p className="text-sm text-muted-foreground py-16 text-center">No data yet. Add an entry below to see the trend.</p>
        ) : (
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <LineChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#26262B" vertical={false} />
                <XAxis dataKey="date" stroke="#66666E" fontSize={11} tickLine={false} axisLine={{ stroke: "#26262B" }} />
                <YAxis stroke="#66666E" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "#1A1A1C", border: "1px solid #26262B", borderRadius: 6, fontSize: 12 }} labelStyle={{ color: "#F2F2F2" }} />
                <Line type="monotone" dataKey={metric} stroke={activeMetric.color} strokeWidth={2.5} dot={{ r: 3, fill: activeMetric.color }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      {/* Add row */}
      <section className="bg-[#121214] border border-border rounded-lg p-6 mb-6">
        <h3 className="font-display font-bold tracking-tight mb-4">Log a data point</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Input label="Date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="perf-date-input" />
          <Input label="Views" type="number" value={form.views} onChange={(v) => setForm({ ...form, views: v })} testid="perf-views-input" />
          <Input label="Subscribers" type="number" value={form.subscribers} onChange={(v) => setForm({ ...form, subscribers: v })} testid="perf-subs-input" />
          <Input label="Videos" type="number" value={form.videos_published} onChange={(v) => setForm({ ...form, videos_published: v })} testid="perf-videos-input" />
          <div className="flex items-end">
            <button onClick={add} data-testid="perf-add-button"
              className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-bold rounded-md px-4 py-2.5 text-sm hover:bg-[#C29035] transition-colors duration-200">
              <Plus size={16} /> Log
            </button>
          </div>
        </div>
      </section>

      {/* Table */}
      <section className="bg-[#121214] border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <Th>Date</Th><Th right>Views</Th><Th right>Subscribers</Th><Th right>Videos</Th><Th right></Th>
              </tr>
            </thead>
            <tbody data-testid="perf-table-body">
              {rows.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-muted-foreground py-10">No entries logged.</td></tr>
              ) : (
                [...rows].reverse().map((r) => (
                  <tr key={r.id} className="border-b border-border/50 hover:bg-secondary/40 transition-colors duration-200">
                    <td className="px-5 py-3 font-mono text-xs">{new Date(r.date).toLocaleDateString()}</td>
                    <td className="px-5 py-3 font-mono text-right">{r.views.toLocaleString()}</td>
                    <td className="px-5 py-3 font-mono text-right">{r.subscribers.toLocaleString()}</td>
                    <td className="px-5 py-3 font-mono text-right">{r.videos_published}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => del(r.id)} data-testid={`delete-perf-${r.id}`} className="text-muted-foreground hover:text-destructive transition-colors duration-200"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Th({ children, right }) {
  return <th className={`px-5 py-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground ${right ? "text-right" : ""}`}>{children}</th>;
}

function Input({ label, type = "text", value, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted-foreground mb-1.5">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-[#0A0A0B] border border-border rounded-md px-3 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors duration-200" />
    </div>
  );
}
